import { clamp } from '../config/game-config'
import { healthColor, healthRatio } from '../domain/rules'
import type { ArenaSnapshot, BobbleSnapshot, Point } from '../domain/types'
import type { WorldEvent } from '../physics/game-world'

interface RingEffect extends Point {
  age: number
  duration: number
  radius: number
  hue: number
}

interface Particle extends Point {
  vx: number
  vy: number
  age: number
  duration: number
  size: number
  hue: number
}

const TAU = Math.PI * 2

export class GameRenderer {
  private readonly context: CanvasRenderingContext2D
  private readonly rings: RingEffect[] = []
  private readonly particles: Particle[] = []
  private width = 1
  private height = 1
  private shakeStrength = 0
  private reducedMotion = false

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Canvas 2D is not available')
    this.context = context
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }

  resize(width: number, height: number): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.width = Math.max(1, width)
    this.height = Math.max(1, height)
    this.canvas.width = Math.round(this.width * dpr)
    this.canvas.height = Math.round(this.height * dpr)
    this.context.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  addExplosion(point: Point, protectedTarget: boolean): void {
    const hue = protectedTarget ? 186 : 322
    this.rings.push({ ...point, age: 0, duration: 0.48, radius: 180, hue })
    this.rings.push({ ...point, age: -0.06, duration: 0.34, radius: 110, hue: hue + 34 })
    this.spawnParticles(point, this.reducedMotion ? 8 : 26, hue, 120, 420)
    this.shakeStrength = this.reducedMotion ? 1 : 8
  }

  handleWorldEvent(event: WorldEvent): void {
    if (event.type === 'damage') {
      this.spawnParticles(event.at, this.reducedMotion ? 2 : 5, 28, 35, 130)
      return
    }

    this.rings.push({ ...event.at, age: 0, duration: 0.58, radius: 120, hue: 6 })
    this.spawnParticles(event.at, this.reducedMotion ? 10 : 34, event.kind === 'square' ? 190 : 322, 90, 360)
    this.shakeStrength = Math.max(this.shakeStrength, this.reducedMotion ? 1 : 12)
  }

  update(deltaSeconds: number): void {
    for (const ring of this.rings) ring.age += deltaSeconds
    for (const particle of this.particles) {
      particle.age += deltaSeconds
      particle.x += particle.vx * deltaSeconds
      particle.y += particle.vy * deltaSeconds
      particle.vx *= 0.97
      particle.vy *= 0.97
    }
    this.removeExpired(this.rings)
    this.removeExpired(this.particles)
    this.shakeStrength *= Math.pow(0.06, deltaSeconds)
    if (this.shakeStrength < 0.08) this.shakeStrength = 0
  }

  render(snapshot: ArenaSnapshot): void {
    const context = this.context
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    context.setTransform(dpr, 0, 0, dpr, 0, 0)
    context.clearRect(0, 0, this.width, this.height)

    const shakeX = this.reducedMotion ? 0 : (Math.random() - 0.5) * this.shakeStrength
    const shakeY = this.reducedMotion ? 0 : (Math.random() - 0.5) * this.shakeStrength
    context.save()
    context.translate(shakeX, shakeY)
    this.drawBackground(context)
    this.drawRings(context)

    for (const bobble of snapshot.bobbles) this.drawBobble(context, bobble)
    this.drawParticles(context)
    this.drawArenaEdge(context)
    context.restore()
  }

  private drawBackground(context: CanvasRenderingContext2D): void {
    const gradient = context.createRadialGradient(
      this.width * 0.5,
      this.height * 0.42,
      0,
      this.width * 0.5,
      this.height * 0.5,
      Math.max(this.width, this.height) * 0.78,
    )
    gradient.addColorStop(0, '#101739')
    gradient.addColorStop(0.48, '#090c20')
    gradient.addColorStop(1, '#04050d')
    context.fillStyle = gradient
    context.fillRect(-20, -20, this.width + 40, this.height + 40)

    const spacing = clamp(Math.min(this.width, this.height) / 12, 34, 72)
    context.strokeStyle = 'rgba(91, 116, 202, 0.075)'
    context.lineWidth = 1
    context.beginPath()
    for (let x = spacing; x < this.width; x += spacing) {
      context.moveTo(x, 0)
      context.lineTo(x, this.height)
    }
    for (let y = spacing; y < this.height; y += spacing) {
      context.moveTo(0, y)
      context.lineTo(this.width, y)
    }
    context.stroke()
  }

  private drawBobble(context: CanvasRenderingContext2D, bobble: BobbleSnapshot): void {
    const ratio = healthRatio(bobble)
    const color = healthColor(bobble)
    const identityColor = bobble.kind === 'square' ? '#59f7ff' : '#ff3cac'
    const radius = bobble.size / 2

    context.save()
    context.translate(bobble.position.x, bobble.position.y)
    context.rotate(bobble.angle)
    context.shadowColor = color
    context.shadowBlur = 11 + ratio * 12
    context.lineWidth = clamp(bobble.size * 0.085, 2.5, 5)
    context.strokeStyle = color
    context.fillStyle = 'rgba(5, 9, 24, 0.88)'

    context.beginPath()
    if (bobble.kind === 'circle') {
      context.arc(0, 0, radius * 0.82, 0, TAU)
    } else {
      const side = radius * 1.56
      context.roundRect(-side / 2, -side / 2, side, side, Math.max(4, side * 0.12))
    }
    context.fill()
    context.stroke()

    context.shadowBlur = 7
    context.strokeStyle = identityColor
    context.globalAlpha = 0.58
    context.lineWidth = 1.5
    context.beginPath()
    if (bobble.kind === 'circle') {
      context.arc(0, 0, radius * 0.47, 0, TAU)
    } else {
      const arm = radius * 0.34
      context.moveTo(-arm, 0)
      context.lineTo(arm, 0)
      context.moveTo(0, -arm)
      context.lineTo(0, arm)
    }
    context.stroke()

    context.globalAlpha = 0.9
    context.fillStyle = color
    context.shadowBlur = 10
    context.beginPath()
    context.arc(radius * 0.25, -radius * 0.23, Math.max(2.2, radius * 0.075), 0, TAU)
    context.fill()
    context.restore()
  }

  private drawRings(context: CanvasRenderingContext2D): void {
    context.save()
    context.globalCompositeOperation = 'lighter'
    for (const ring of this.rings) {
      if (ring.age < 0) continue
      const progress = clamp(ring.age / ring.duration, 0, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const alpha = 1 - progress
      context.beginPath()
      context.arc(ring.x, ring.y, 10 + ring.radius * eased, 0, TAU)
      context.strokeStyle = `hsla(${ring.hue} 100% 67% / ${alpha * 0.9})`
      context.lineWidth = 2 + (1 - progress) * 7
      context.shadowColor = `hsl(${ring.hue} 100% 65%)`
      context.shadowBlur = 22 * alpha
      context.stroke()
    }
    context.restore()
  }

  private drawParticles(context: CanvasRenderingContext2D): void {
    context.save()
    context.globalCompositeOperation = 'lighter'
    for (const particle of this.particles) {
      const progress = clamp(particle.age / particle.duration, 0, 1)
      context.globalAlpha = 1 - progress
      context.fillStyle = `hsl(${particle.hue} 100% 68%)`
      context.shadowColor = context.fillStyle
      context.shadowBlur = 9
      context.beginPath()
      context.arc(particle.x, particle.y, particle.size * (1 - progress * 0.7), 0, TAU)
      context.fill()
    }
    context.restore()
  }

  private drawArenaEdge(context: CanvasRenderingContext2D): void {
    const edge = context.createLinearGradient(0, 0, this.width, this.height)
    edge.addColorStop(0, 'rgba(89, 247, 255, 0.62)')
    edge.addColorStop(0.5, 'rgba(102, 119, 255, 0.25)')
    edge.addColorStop(1, 'rgba(255, 60, 172, 0.58)')
    context.strokeStyle = edge
    context.lineWidth = 2
    context.shadowColor = '#596fff'
    context.shadowBlur = 15
    context.strokeRect(1, 1, this.width - 2, this.height - 2)
  }

  private spawnParticles(
    point: Point,
    count: number,
    hue: number,
    minimumSpeed: number,
    maximumSpeed: number,
  ): void {
    for (let index = 0; index < count; index += 1) {
      const angle = Math.random() * TAU
      const speed = minimumSpeed + Math.random() * (maximumSpeed - minimumSpeed)
      this.particles.push({
        ...point,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        age: 0,
        duration: 0.28 + Math.random() * 0.42,
        size: 1.2 + Math.random() * 3.4,
        hue: hue + (Math.random() - 0.5) * 44,
      })
    }
  }

  private removeExpired<T extends { age: number; duration: number }>(items: T[]): void {
    for (let index = items.length - 1; index >= 0; index -= 1) {
      const item = items[index]
      if (item && item.age >= item.duration) items.splice(index, 1)
    }
  }
}
